/**
 * Shared project-fields form for ALL intake paths — the manual form
 * (/projects/new), PDF fusion (/projects/new/pdf), and chat extraction
 * (/projects/new/chat) — so the fields, ordering, and validation are identical
 * regardless of how the draft was produced (§L). Controlled: the parent owns the
 * `draft` and applies edits via `onPatch`. Never auto-writes — the create call
 * happens in the parent's onSubmit.
 *
 * Two layouts over the SAME fields/state/validation (post-seventh-demo, W1):
 *   - 'scroll' (default) — one long card; used by chat, PDF, and the calendar
 *     modal review.
 *   - 'wizard' — a role-aware guided stepper (the real version of the
 *     ProjectNewWizardMockup) used by the /projects/new page. PM = 4 steps
 *     (no bid/sign-off); supervisor/admin = 5 steps (adds Bid & approval).
 *
 * Required-field gate (Leo, 2026-06-15): title, client, full site address
 * (line 1 / city / state / ZIP), site contact name + phone, a requested start
 * day with working hours, and a hard deadline. Submit is disabled until present.
 *
 * Task breakdown: rendered inline ONLY when task props are passed (supervisor /
 * admin). PMs are requesters, not operators, so callers omit them for a PM and
 * the breakdown is proposed server-side from the project kind.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  ClipboardList,
  DollarSign,
  FileCheck2,
  Loader2,
  MapPin,
  TriangleAlert,
  X,
} from 'lucide-react';
import type {
  CrewRoleLines,
  IntakeProposedTask,
  ProjectCreateInput,
  ProjectKind,
  ScheduleDay,
} from '@vari-crew/schemas';
import { summarizeLabor } from '@vari-crew/schemas';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input, Label, Textarea } from '@/components/ui/Input';
import { CalendarDatePicker } from '@/components/ui/CalendarDatePicker';
import { DailyHoursPicker, isWeekendDay } from '@/components/ui/DailyHoursPicker';
import { AddressAutocomplete } from '@/components/ui/AddressAutocomplete';
import { RecipientAutocomplete } from '@/components/ui/RecipientAutocomplete';
import { BidBuilder, deriveLaborSeed, type BidInputs } from '@/components/ui/BidBuilder';
import type { RoutingContext } from '@/components/ui/MileageCalculatorModal';
import { RecipientPreview } from '@/components/bid/RecipientPreview';
import { ProposedTasksEditor } from '@/components/intake/ProposedTasksEditor';
import { TimingAuthoring } from '@/components/intake/timing/TimingAuthoring';
import type { TimingModel } from '@/components/intake/timing/timingModel';
import { OtherRecipientsEditor } from '@/components/intake/OtherRecipientsEditor';
import { useRecipients } from '@/api/recipients';
import { useCostSettings } from '@/api/cost-settings';
import { useLaborRates } from '@/api/labor-rates';
import { useLocations } from '@/api/locations';
import { dayToIso, dayTimeToIso, isoToDay } from '@/lib/calendarGrid';
import { cn } from '@/lib/cn';

type Address = NonNullable<ProjectCreateInput['site_address']>;

// The PM picks a pickup origin from the managed warehouse list (Settings →
// Warehouses, DB-driven via useLocations); "Other" names one not in the list.
const OTHER_WAREHOUSE = '__other__';

export const KIND_OPTIONS: { value: ProjectKind; label: string }[] = [
  { value: 'install', label: 'Install — delivery + install' },
  { value: 'reconfigure', label: 'Reconfigure — rework existing' },
  { value: 'punch', label: 'Punch / warranty visit' },
  { value: 'service', label: 'Service / labor call' },
  { value: 'pickup', label: 'Pickup / removal' },
  { value: 'other', label: 'Other' },
];

export const PRIORITY_OPTIONS: { value: NonNullable<ProjectCreateInput['priority']>; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const EMPTY_ADDRESS: Address = {
  line1: '',
  line2: null,
  city: '',
  state: '',
  postal_code: '',
  country: 'US',
  latitude: null,
  longitude: null,
};

/**
 * HIDDEN FOR NOW — the legacy per-task crew/hours editor (<ProposedTasksEditor>).
 *
 * As of the post-sixth-demo work (2026-06-23), crew + hours are set per task/day
 * in the "Plan the work" studio (TimingAuthoring), which is the single source for
 * the bid labor. This standalone TASKS table duplicated that (two places to enter
 * crew), so it's hidden to avoid confusion. It is intentionally NOT deleted: we
 * haven't decided whether tasks still need a dedicated name/details list separate
 * from the studio. Flip this to `true` to bring the editor back.
 *
 * Note: `proposedTasks` is unaffected — it's still seeded from the project kind,
 * sent as `proposed_tasks` on create, and used to seed the studio's task list;
 * only this editor's UI is suppressed.
 */
const SHOW_LEGACY_TASKS = false;

export interface ProjectDraftReviewProps {
  draft: Partial<ProjectCreateInput>;
  onPatch: <K extends keyof ProjectCreateInput>(
    key: K,
    value: Partial<ProjectCreateInput>[K],
  ) => void;
  onSubmit: () => void;
  onReset?: () => void;
  submitting?: boolean;
  source?: 'stub' | 'gemini' | 'mixed';
  heading?: string;
  submitLabel?: string;
  /**
   * Layout for the same fields/state/validation. 'scroll' (default) is the one
   * long card used by chat, PDF, and the calendar modal; 'wizard' is the
   * role-aware guided stepper used by the /projects/new page.
   */
  layout?: 'scroll' | 'wizard';
  /** Non-blocking cross-document warnings to surface above the form. */
  warnings?: string[];
  /**
   * Editable task plan — supervisor/admin only (PMs are requesters, not
   * operators, so callers omit these for a PM). When provided, an inline
   * task editor renders and its list is sent as POST /projects `proposed_tasks`.
   */
  proposedTasks?: IntakeProposedTask[];
  onProposedTasksChange?: (next: IntakeProposedTask[]) => void;
  /**
   * Persisted "Plan the work" authoring model (supervisor only) — held by the
   * panel in the intake session cache + server draft so the full per-block
   * roles/times/descriptions survive a remount or a resumed draft, not just the
   * flattened proposed_tasks. `initialTimingModel` rehydrates the studio;
   * `onTimingModelChange` reports each edit back for persistence.
   */
  initialTimingModel?: TimingModel | null;
  onTimingModelChange?: (model: TimingModel) => void;
  /** Fired alongside onPatch('kind') so the caller can re-seed the task plan. */
  onKindChange?: (kind: ProjectKind) => void;
  /**
   * Show the supervisor-only "require sign-off before scheduling" section (§A6).
   * Callers pass true for supervisor/admin (same gate as the task editor); for a
   * PM it's omitted — their project is already a request awaiting approval.
   */
  allowSignoff?: boolean;
  /**
   * Optional content rendered inside the card, just above the submit row — used
   * by the manual form for its document uploader (chat/PDF have their own). In
   * wizard layout it lands on the final Review step.
   */
  footer?: React.ReactNode;
}

export function ProjectDraftReview({
  draft,
  onPatch,
  onSubmit,
  onReset,
  submitting = false,
  source,
  heading = 'Review extracted draft',
  submitLabel = 'Create project',
  layout = 'scroll',
  warnings,
  proposedTasks,
  onProposedTasksChange,
  initialTimingModel,
  onTimingModelChange,
  onKindChange,
  allowSignoff = false,
  footer,
}: ProjectDraftReviewProps) {
  // Directory pool for the contact + status-recipient autocompletes (same source
  // as the bid recipient). Available to PMs too now (the endpoint allows pm).
  const { data: recipients } = useRecipients();
  // Pickup-origin options come from the managed warehouse registry (DB-driven,
  // Settings → Warehouses) so a newly added warehouse is selectable here.
  const { data: warehouseList } = useLocations('vari_warehouse');
  const warehouseOptions = warehouseList?.data.map((w) => w.name) ?? [];
  function patchAddress(field: keyof Address, value: string) {
    onPatch('site_address', {
      ...(draft.site_address ?? EMPTY_ADDRESS),
      [field]: value,
    });
  }

  // Pickup origins are a list (VCS-203): chips for the chosen warehouses plus an
  // "add" control. `otherWarehouse` (null = hidden) reveals a free-text field for
  // a warehouse not in the preset list. Empty list normalizes to null on submit.
  const [otherWarehouse, setOtherWarehouse] = useState<string | null>(null);
  // Labor derived from the supervisor task plan (Plan the work). Unless the user
  // overrides it, push it into the bid so the Labor reflects the crew/role mix.
  const [derivedLabor, setDerivedLabor] = useState<{ roles: CrewRoleLines; cents: number } | null>(
    null,
  );
  const [laborOverride, setLaborOverride] = useState(false);
  useEffect(() => {
    if (!laborOverride && derivedLabor) {
      onPatch('bid_labor_roles', derivedLabor.roles);
      onPatch('bid_labor_cents', derivedLabor.cents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [derivedLabor, laborOverride]);
  const origins = draft.origin_warehouse ?? [];
  const presetsToAdd = warehouseOptions.filter((w) => !origins.includes(w));
  const addWarehouse = (name: string) => {
    const w = name.trim();
    if (!w || origins.includes(w)) return;
    onPatch('origin_warehouse', [...origins, w]);
  };
  const removeWarehouse = (name: string) => {
    const next = origins.filter((w) => w !== name);
    onPatch('origin_warehouse', next.length ? next : null);
  };

  // Requested timing now comes from the per-day schedule (working hours per day);
  // requested_start_date/end_date are derived (first/last day) so the gate and
  // the create payload stay in sync.
  const startDay = isoToDay(draft.requested_start_date);
  const endDay = isoToDay(draft.requested_end_date);
  const schedule = draft.requested_schedule ?? null;
  // Weekend toggle shared by the calendar + per-day hours. Auto-turns on when the
  // extracted/edited schedule already names a weekend day (e.g. chat/PDF fill).
  const [includeWeekends, setIncludeWeekends] = useState(() =>
    (schedule ?? []).some((s) => isWeekendDay(s.date)),
  );
  useEffect(() => {
    if ((draft.requested_schedule ?? []).some((s) => isWeekendDay(s.date))) setIncludeWeekends(true);
  }, [draft.requested_schedule]);

  function patchSchedule(next: ScheduleDay[] | null) {
    onPatch('requested_schedule', next);
    if (next && next.length > 0) {
      const f = next[0]!;
      const l = next[next.length - 1]!;
      onPatch('requested_start_date', dayTimeToIso(f.date, f.start_time));
      onPatch('requested_end_date', next.length > 1 ? dayTimeToIso(l.date, l.end_time) : null);
    } else {
      onPatch('requested_start_date', undefined);
      onPatch('requested_end_date', null);
    }
  }

  // Required-before-submit gate (VCS-201/202 + Leo 2026-06-15), split per wizard
  // step so the step rail can flag where required fields are still missing. The
  // flat list (same order) drives the submit gate + the "missing" hint.
  const addr = draft.site_address;
  const missBasics = [
    !draft.title?.trim() && 'project title',
    !draft.client_name?.trim() && 'client name',
  ].filter(Boolean) as string[];
  const missSite = [
    !addr?.line1?.trim() && 'site address',
    !addr?.city?.trim() && 'city',
    !addr?.state?.trim() && 'state',
    !addr?.postal_code?.trim() && 'ZIP',
    !draft.site_contact_name?.trim() && 'site contact',
    !draft.site_contact_phone?.trim() && 'site contact phone',
  ].filter(Boolean) as string[];
  const missTiming = [
    !(schedule && schedule.length > 0) && 'requested start day & hours',
    !draft.deadline_date && 'hard deadline',
  ].filter(Boolean) as string[];
  const missingRequired = [...missBasics, ...missSite, ...missTiming];
  const missingByStep: Record<string, string[]> = {
    basics: missBasics,
    site: missSite,
    timing: missTiming,
  };
  const canSubmit = missingRequired.length === 0 && !submitting;

  // --- Section nodes — composed differently by the two layouts but identical
  //     fields/handlers either way. ---

  const warningsNode =
    warnings && warnings.length > 0 ? (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 space-y-1">
        {warnings.map((w, i) => (
          <div key={i} className="flex items-start gap-2 text-xs text-amber-900">
            <TriangleAlert className="w-3.5 h-3.5 text-amber-700 mt-0.5 shrink-0" />
            <span>{w}</span>
          </div>
        ))}
      </div>
    ) : null;

  const basicsNode = (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Project kind</Label>
          <select
            value={draft.kind ?? 'install'}
            onChange={(e) => {
              const v = e.target.value as ProjectKind;
              onPatch('kind', v);
              onKindChange?.(v);
            }}
            className="w-full h-9 rounded-md border border-line bg-transparent px-2 text-sm"
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Label>Priority</Label>
          <select
            value={draft.priority ?? 'normal'}
            onChange={(e) => onPatch('priority', e.target.value as ProjectCreateInput['priority'])}
            className="w-full h-9 rounded-md border border-line bg-transparent px-2 text-sm"
          >
            {PRIORITY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <p className="text-[11px] text-ink-faint -mt-1">
        Tells the supervisor what kind of work this is. Fields a kind doesn&apos;t use can stay
        blank.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Project title" required>
          <Input value={draft.title ?? ''} onChange={(e) => onPatch('title', e.target.value)} />
        </Field>
        <Field label="Client name" required>
          <Input
            value={draft.client_name ?? ''}
            onChange={(e) => onPatch('client_name', e.target.value)}
          />
        </Field>
        <Field label="SAP order #">
          <Input
            value={draft.sap_order_number ?? ''}
            onChange={(e) => onPatch('sap_order_number', e.target.value || null)}
          />
        </Field>
        <Field label="SAP project ID">
          <Input
            value={draft.sap_project_id ?? ''}
            onChange={(e) => onPatch('sap_project_id', e.target.value || null)}
          />
        </Field>
        <Field label="Customer #">
          <Input
            value={draft.customer_number ?? ''}
            onChange={(e) => onPatch('customer_number', e.target.value || null)}
          />
        </Field>
        <Field label="Design job #">
          <Input
            value={draft.design_job_number ?? ''}
            onChange={(e) => onPatch('design_job_number', e.target.value || null)}
          />
        </Field>
        <Field label="Design PD #">
          <Input
            value={draft.design_pd_number ?? ''}
            onChange={(e) => onPatch('design_pd_number', e.target.value || null)}
          />
        </Field>
        <Field label="Proposed budget (USD)">
          <Input
            type="number"
            step="0.01"
            value={
              draft.delivery_install_budget_cents != null
                ? (draft.delivery_install_budget_cents / 100).toFixed(2)
                : ''
            }
            onChange={(e) => {
              const v = e.target.value ? Math.round(parseFloat(e.target.value) * 100) : null;
              onPatch('delivery_install_budget_cents', v ?? null);
            }}
          />
        </Field>
        <Field label="Interior designer">
          <RecipientAutocomplete
            value={draft.interior_designer ?? ''}
            onChange={(name) => {
              onPatch('interior_designer', name || null);
              onPatch('interior_designer_email', null);
            }}
            onPick={(r) => {
              onPatch('interior_designer', r.display_name);
              onPatch('interior_designer_email', r.email);
            }}
            recipients={recipients?.data ?? []}
            placeholder="Search the directory…"
          />
        </Field>
        <Field label="CSM">
          <RecipientAutocomplete
            value={draft.csm ?? ''}
            onChange={(name) => {
              onPatch('csm', name || null);
              onPatch('csm_email', null);
            }}
            onPick={(r) => {
              onPatch('csm', r.display_name);
              onPatch('csm_email', r.email);
            }}
            recipients={recipients?.data ?? []}
            placeholder="Search the directory…"
          />
        </Field>
        <Field label="Sales executive">
          <RecipientAutocomplete
            value={draft.sales_executive ?? ''}
            onChange={(name) => {
              onPatch('sales_executive', name || null);
              onPatch('sales_executive_email', null);
            }}
            onPick={(r) => {
              onPatch('sales_executive', r.display_name);
              onPatch('sales_executive_email', r.email);
            }}
            recipients={recipients?.data ?? []}
            placeholder="Search the directory…"
          />
        </Field>
      </div>
      <div>
        <Label>Other</Label>
        <p className="text-[11px] text-ink-faint mb-1.5">
          Anyone else who should get project + task status updates as work progresses. Pick from the
          directory or type a custom email — add as many as you need.
        </p>
        <OtherRecipientsEditor
          value={draft.status_update_recipients ?? []}
          onChange={(next) => onPatch('status_update_recipients', next.length ? next : null)}
          recipients={recipients?.data ?? []}
        />
      </div>
    </>
  );

  const siteNode = (
    <>
      <div>
        <Label>
          Site address (line 1) <span className="text-status-blocked-fg">*</span>
        </Label>
        <AddressAutocomplete
          value={draft.site_address?.line1 ?? ''}
          onChange={(v) => patchAddress('line1', v)}
          onSelect={(s) =>
            onPatch('site_address', {
              ...(draft.site_address ?? EMPTY_ADDRESS),
              line1: s.line1,
              city: s.city || (draft.site_address?.city ?? ''),
              state: s.state || (draft.site_address?.state ?? ''),
              postal_code: s.postal_code || (draft.site_address?.postal_code ?? ''),
              latitude: s.latitude,
              longitude: s.longitude,
            })
          }
        />
      </div>
      <div>
        <Label>Suite / floor / building (line 2)</Label>
        <Input
          value={draft.site_address?.line2 ?? ''}
          onChange={(e) =>
            onPatch('site_address', {
              ...(draft.site_address ?? EMPTY_ADDRESS),
              line2: e.target.value || null,
            })
          }
          placeholder="Suite 200, 3rd floor, Building C…"
        />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Field label="City" required>
          <Input
            value={draft.site_address?.city ?? ''}
            onChange={(e) => patchAddress('city', e.target.value)}
          />
        </Field>
        <Field label="State" required>
          <Input
            value={draft.site_address?.state ?? ''}
            onChange={(e) => patchAddress('state', e.target.value)}
          />
        </Field>
        <Field label="ZIP" required>
          <Input
            value={draft.site_address?.postal_code ?? ''}
            onChange={(e) => patchAddress('postal_code', e.target.value)}
          />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Site contact name" required>
          <Input
            value={draft.site_contact_name ?? ''}
            onChange={(e) => onPatch('site_contact_name', e.target.value || null)}
          />
        </Field>
        <Field label="Site contact phone" required>
          <Input
            value={draft.site_contact_phone ?? ''}
            onChange={(e) => onPatch('site_contact_phone', e.target.value || null)}
            placeholder="(555) 123-4567 ext. 12"
          />
        </Field>
      </div>
      <div>
        <Label>Site notes</Label>
        <Textarea
          rows={2}
          value={draft.site_notes ?? ''}
          onChange={(e) => onPatch('site_notes', e.target.value || null)}
          placeholder="Gate code, parking, dock access, access between two suites…"
        />
      </div>
      <div>
        <Label>Origin warehouse{origins.length > 1 ? 's' : ''}</Label>
        {origins.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {origins.map((w) => (
              <span
                key={w}
                className="inline-flex items-center gap-1 rounded-full border border-line bg-fill-soft px-2 py-0.5 text-xs"
              >
                {w}
                <button
                  type="button"
                  onClick={() => removeWarehouse(w)}
                  aria-label={`Remove ${w}`}
                  className="text-ink-faint hover:text-ink"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        <select
          value=""
          onChange={(e) => {
            const v = e.target.value;
            if (v === OTHER_WAREHOUSE) setOtherWarehouse('');
            else if (v) addWarehouse(v);
          }}
          className="w-full h-9 rounded-md border border-line bg-transparent px-2 text-sm"
        >
          <option value="">＋ Add warehouse…</option>
          {presetsToAdd.map((w) => (
            <option key={w} value={w}>
              {w}
            </option>
          ))}
          <option value={OTHER_WAREHOUSE}>Other warehouse…</option>
        </select>
        {otherWarehouse !== null && (
          <div className="flex gap-2 mt-1.5">
            <Input
              value={otherWarehouse}
              onChange={(e) => setOtherWarehouse(e.target.value)}
              placeholder="Name the warehouse"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                addWarehouse(otherWarehouse);
                setOtherWarehouse(null);
              }}
            >
              Add
            </Button>
          </div>
        )}
        <p className="text-[11px] text-ink-faint mt-1">
          Where the crew picks up — matched from the packing slip. Add every origin if items ship
          from more than one warehouse.
        </p>
      </div>
    </>
  );

  const timingNode = (
    <div className="space-y-2">
      <Label>
        Requested timing <span className="text-status-blocked-fg">*</span>
      </Label>
      <p className="text-[11px] text-ink-faint -mt-1">
        Pick a start day (and an end day if it spans more than one) and the working hours — the hours
        the crew is allowed on site, same every day or different per day. The supervisor confirms or
        proposes on approval.
      </p>
      <CalendarDatePicker
        start={startDay}
        end={endDay}
        onChange={({ start, end }) => {
          onPatch('requested_start_date', start ? dayToIso(start) : undefined);
          onPatch('requested_end_date', end ? dayToIso(end) : null);
        }}
        includeWeekends={includeWeekends}
        onIncludeWeekendsChange={setIncludeWeekends}
      />
      {allowSignoff ? (
        // Supervisor/admin: two-approach authoring (post-sixth-demo) — a
        // "Start from days / tasks" toggle over a shared model. Days mode
        // renders the working-hours picker + task placement; tasks mode leads
        // with task estimates and derives the schedule. PMs get the plain
        // working-hours picker below.
        <TimingAuthoring
          start={startDay}
          end={endDay}
          schedule={schedule}
          onScheduleChange={patchSchedule}
          includeWeekends={includeWeekends}
          flexible={draft.requested_schedule_flexible ?? false}
          onFlexibleChange={(v) => onPatch('requested_schedule_flexible', v)}
          kind={draft.kind ?? 'install'}
          clientLabel={draft.client_name ?? undefined}
          proposedTasks={proposedTasks}
          initialModel={initialTimingModel}
          onModelChange={onTimingModelChange}
          onLaborChange={setDerivedLabor}
          onProposedTasksChange={onProposedTasksChange}
        />
      ) : (
        <DailyHoursPicker
          start={startDay}
          end={endDay}
          value={schedule}
          onChange={patchSchedule}
          flexible={draft.requested_schedule_flexible ?? false}
          onFlexibleChange={(v) => onPatch('requested_schedule_flexible', v)}
          includeWeekends={includeWeekends}
        />
      )}
      <div className="max-w-xs">
        <Label>
          Hard deadline <span className="text-status-blocked-fg">*</span>
        </Label>
        <Input
          type="date"
          value={isoToDay(draft.deadline_date) ?? ''}
          onChange={(e) =>
            onPatch('deadline_date', e.target.value ? dayToIso(e.target.value) : undefined)
          }
        />
      </div>
    </div>
  );

  const notesNode = (
    <div>
      <Label>Intake notes</Label>
      <Textarea
        rows={3}
        value={draft.intake_notes ?? ''}
        onChange={(e) => onPatch('intake_notes', e.target.value)}
      />
    </div>
  );

  // Hidden for now — superseded by the Plan-the-work studio. See SHOW_LEGACY_TASKS.
  const legacyTasksNode =
    SHOW_LEGACY_TASKS && proposedTasks && onProposedTasksChange ? (
      <ProposedTasksEditor
        value={proposedTasks}
        onChange={onProposedTasksChange}
        clientLabel={draft.client_name ?? undefined}
        budgetCents={draft.delivery_install_budget_cents ?? null}
      />
    ) : null;

  const bidNode = allowSignoff ? (
    <BidSection
      draft={draft}
      onPatch={onPatch}
      proposedTasks={proposedTasks}
      override={laborOverride}
      onOverrideChange={setLaborOverride}
    />
  ) : null;

  const signoffNode = allowSignoff ? <SignoffSection draft={draft} onPatch={onPatch} /> : null;

  const submitRow = (
    <div className="pt-2 space-y-2">
      {missingRequired.length > 0 && (
        <p className="text-[11px] text-amber-700 text-right">
          Required before submitting: {missingRequired.join(', ')}.
        </p>
      )}
      <div className="flex justify-end gap-2">
        {onReset && (
          <Button variant="outline" onClick={onReset} disabled={submitting}>
            Reset
          </Button>
        )}
        <Button onClick={onSubmit} disabled={!canSubmit}>
          {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {submitLabel}
          <ArrowRight className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );

  if (layout === 'wizard') {
    return (
      <IntakeWizardShell
        allowSignoff={allowSignoff}
        submitLabel={submitLabel}
        submitting={submitting}
        canSubmit={canSubmit}
        onSubmit={onSubmit}
        onReset={onReset}
        warningsNode={warningsNode}
        missingRequired={missingRequired}
        missingByStep={missingByStep}
        sections={{
          basics: basicsNode,
          site: siteNode,
          timing: timingNode,
          plan: (
            <>
              {legacyTasksNode}
              {bidNode}
              {signoffNode}
            </>
          ),
          review: (
            <>
              {notesNode}
              {footer}
            </>
          ),
        }}
      />
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-sm">{heading}</CardTitle>
        {source && (
          <span
            className="text-[10px] font-mono text-ink-faint uppercase tracking-wide"
            title="Where these fields came from"
          >
            {source}
          </span>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        {warningsNode}
        {basicsNode}
        {siteNode}
        {timingNode}
        {notesNode}
        {legacyTasksNode}
        {bidNode}
        {signoffNode}
        {footer}
        {submitRow}
      </CardContent>
    </Card>
  );
}

type WizardStepKey = 'basics' | 'site' | 'timing' | 'plan' | 'review';
type WizardStepDef = { key: WizardStepKey; label: string; hint: string; Icon: typeof ClipboardList };

const SUP_STEPS: WizardStepDef[] = [
  { key: 'basics', label: 'Basics', hint: 'Project, client & people', Icon: ClipboardList },
  { key: 'site', label: 'Site & contact', hint: 'Where & who on site', Icon: MapPin },
  { key: 'timing', label: 'Timing & plan', hint: 'Dates & crew plan', Icon: CalendarDays },
  { key: 'plan', label: 'Bid & approval', hint: 'Quote & sign-off', Icon: DollarSign },
  { key: 'review', label: 'Review & submit', hint: 'Confirm & create', Icon: FileCheck2 },
];
const PM_STEPS: WizardStepDef[] = [
  { key: 'basics', label: 'Basics', hint: 'Project, client & people', Icon: ClipboardList },
  { key: 'site', label: 'Site & contact', hint: 'Where & who on site', Icon: MapPin },
  { key: 'timing', label: 'Timing', hint: 'Dates & working hours', Icon: CalendarDays },
  { key: 'review', label: 'Review & submit', hint: 'Confirm & submit', Icon: FileCheck2 },
];

/**
 * Guided-steps shell for the /projects/new page (the real version of
 * ProjectNewWizardMockup). Renders the same section nodes one step at a time
 * with a step rail (missing-field dots), progress, and Back/Continue/Submit.
 * PM = 4 steps; supervisor/admin = 5 (adds the Bid & approval step).
 */
function IntakeWizardShell({
  allowSignoff,
  submitLabel,
  submitting,
  canSubmit,
  onSubmit,
  onReset,
  warningsNode,
  missingRequired,
  missingByStep,
  sections,
}: {
  allowSignoff: boolean;
  submitLabel: string;
  submitting: boolean;
  canSubmit: boolean;
  onSubmit: () => void;
  onReset?: () => void;
  warningsNode: React.ReactNode;
  missingRequired: string[];
  missingByStep: Record<string, string[]>;
  sections: Record<WizardStepKey, React.ReactNode>;
}) {
  const steps = allowSignoff ? SUP_STEPS : PM_STEPS;
  const [step, setStep] = useState(0);
  const last = steps.length - 1;
  const safeStep = Math.min(step, last);
  const current = steps[safeStep]!;

  return (
    <div className="space-y-4">
      {warningsNode}
      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5 items-start">
        {/* Step rail */}
        <aside className="md:sticky md:top-6 self-start">
          <ol className="space-y-1">
            {steps.map((s, i) => {
              const done = i < safeStep;
              const active = i === safeStep;
              const stepMissing = (missingByStep[s.key]?.length ?? 0) > 0;
              return (
                <li key={s.key}>
                  <button
                    type="button"
                    onClick={() => setStep(i)}
                    className={cn(
                      'w-full flex items-center gap-2.5 rounded-md px-2.5 py-2 text-left transition-colors',
                      active ? 'bg-accent/5' : 'hover:bg-fill-soft',
                    )}
                  >
                    <span
                      className={cn(
                        'shrink-0 grid place-items-center w-6 h-6 rounded-full text-[11px] font-medium',
                        done || active
                          ? 'bg-accent text-accent-fg'
                          : 'border border-line text-ink-faint',
                      )}
                    >
                      {done ? <Check className="w-3.5 h-3.5" /> : i + 1}
                    </span>
                    <span className="min-w-0">
                      <span
                        className={cn(
                          'block text-sm leading-tight',
                          active ? 'text-accent font-medium' : 'text-ink',
                        )}
                      >
                        {s.label}
                      </span>
                      <span className="block text-[11px] text-ink-faint truncate">{s.hint}</span>
                    </span>
                    {stepMissing && (
                      <span
                        className="ml-auto shrink-0 w-2 h-2 rounded-full bg-amber-500"
                        title="Required fields to complete"
                        aria-label="Required fields to complete"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>

        {/* Active step */}
        <div className="space-y-3 min-w-0">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <current.Icon className="w-4 h-4 text-ink-faint" />
                {current.label}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {sections[current.key]}
              {current.key === 'review' &&
                (missingRequired.length > 0 ? (
                  <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-xs flex items-start gap-2">
                    <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0" />
                    Required before submitting: {missingRequired.join(', ')}.
                  </div>
                ) : (
                  <div className="rounded-md border border-line bg-fill-soft px-3 py-2 text-xs text-ink-dim">
                    All required fields are present — ready to {allowSignoff ? 'create' : 'submit'}.
                  </div>
                ))}
            </CardContent>
          </Card>

          {/* Footer — progress + nav */}
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-ink-faint whitespace-nowrap">
              Step {safeStep + 1} of {steps.length}
            </span>
            <div className="flex-1 h-1 rounded-full bg-fill-soft overflow-hidden">
              <div
                className="h-1 rounded-full bg-accent transition-all"
                style={{ width: `${((safeStep + 1) / steps.length) * 100}%` }}
              />
            </div>
            {onReset && safeStep === last && (
              <Button variant="outline" size="sm" onClick={onReset} disabled={submitting}>
                Reset
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              disabled={safeStep === 0}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </Button>
            {safeStep < last ? (
              <Button size="sm" onClick={() => setStep((s) => Math.min(last, s + 1))}>
                Continue <ArrowRight className="w-4 h-4" />
              </Button>
            ) : (
              <Button size="sm" onClick={onSubmit} disabled={!canSubmit}>
                {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {submitLabel}
                <Check className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Supervisor-only bid builder (§A6b). The full quoted cost — labor (prefilled
 * from the task estimate), round-trip mileage, and a debris tier → live total —
 * captured at create time so the complete budget rides along on the project
 * (and, with sign-off on, is what the recipient approves). Always shown for
 * supervisors; the live total uses the configured cost settings.
 */
function BidSection({
  draft,
  onPatch,
  proposedTasks,
  override,
  onOverrideChange,
}: {
  draft: Partial<ProjectCreateInput>;
  onPatch: ProjectDraftReviewProps['onPatch'];
  proposedTasks?: IntakeProposedTask[];
  /** When false, Labor is locked to the Plan-the-work crew; true = edit manually. */
  override: boolean;
  onOverrideChange: (v: boolean) => void;
}) {
  const { data: settings } = useCostSettings();
  const { data: rates } = useLaborRates();

  // Labor estimate from the task plan — the same figure the task editor shows —
  // collapsed into a crew × hours seed that prefills the bid's labor calc.
  const laborSeed = useMemo(() => {
    if (!proposedTasks || proposedTasks.length === 0) return null;
    const est = summarizeLabor(
      proposedTasks.map((t) => ({
        type: t.type,
        crew_size_needed: t.crew_size_needed,
        estimated_duration_minutes: t.estimated_duration_minutes,
      })),
      null,
      rates?.estimate_default,
    );
    return deriveLaborSeed(est.total_task_minutes, est.total_person_hours);
  }, [proposedTasks, rates?.estimate_default]);

  const value: BidInputs = {
    labor_cents: draft.bid_labor_cents ?? null,
    labor_roles: draft.bid_labor_roles ?? null,
    mileage_miles: draft.bid_mileage_miles ?? null,
    mileage_legs: draft.bid_mileage_legs ?? null,
    debris_quarters: draft.bid_debris_quarters ?? null,
  };
  const routing: RoutingContext = {
    siteLat: draft.site_address?.latitude ?? null,
    siteLng: draft.site_address?.longitude ?? null,
    siteLabel: draft.title || draft.client_name || 'Job site',
    originWarehouses: draft.origin_warehouse ?? [],
    dayCount: draft.requested_schedule?.length || 1,
    hasDebris: (draft.bid_debris_quarters ?? 0) > 0,
  };

  return (
    <div className="rounded-lg border border-line p-3 space-y-3">
      <div>
        <Label>Bid / budget</Label>
        <p className="text-[11px] text-ink-faint">
          Your quoted cost. Labor is pulled from the crew you set in Plan the work (priced at the
          configured role rates); mileage and debris are set here. This total is the budget tracked
          against actuals, and (with sign-off on) what the recipient approves.
        </p>
      </div>
      <label className="flex items-center gap-2 text-[11px] text-ink-dim">
        <input
          type="checkbox"
          checked={override}
          onChange={(e) => onOverrideChange(e.target.checked)}
        />
        Override labor — edit the crew here instead of using Plan the work
      </label>
      <BidBuilder
        key={override ? 'edit' : 'locked'}
        value={value}
        onChange={(next) => {
          onPatch('bid_labor_cents', next.labor_cents);
          onPatch('bid_labor_roles', next.labor_roles);
          onPatch('bid_mileage_miles', next.mileage_miles);
          onPatch('bid_mileage_legs', next.mileage_legs);
          onPatch('bid_debris_quarters', next.debris_quarters);
        }}
        settings={settings}
        rates={rates ?? null}
        laborSeed={laborSeed}
        routing={routing}
        lockLabor={!override}
      />
    </div>
  );
}

/**
 * Supervisor-only internal sign-off (§A6). Checking the box holds the project as
 * a request and emails an approval link to the chosen recipient instead of
 * scheduling it on create — the "Kelsey case". The bid built above rides along
 * as the total and is shown to the recipient only when opted in.
 */
function SignoffSection({
  draft,
  onPatch,
}: {
  draft: Partial<ProjectCreateInput>;
  onPatch: ProjectDraftReviewProps['onPatch'];
}) {
  const { data: recipients } = useRecipients();
  const { data: settings } = useCostSettings();
  const { data: rates } = useLaborRates();
  const on = draft.require_signoff ?? false;
  const bidValue: BidInputs = {
    labor_cents: draft.bid_labor_cents ?? null,
    labor_roles: draft.bid_labor_roles ?? null,
    mileage_miles: draft.bid_mileage_miles ?? null,
    mileage_legs: draft.bid_mileage_legs ?? null,
    debris_quarters: draft.bid_debris_quarters ?? null,
  };

  return (
    <div className="rounded-lg border border-line bg-fill-soft/50 p-3 space-y-3">
      <label className="flex items-start gap-2 text-sm font-medium">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={on}
          onChange={(e) => onPatch('require_signoff', e.target.checked)}
        />
        <span>
          Require sign-off before scheduling
          <span className="block text-[11px] font-normal text-ink-faint">
            Holds the project as a request and emails an approval link to the recipient (e.g. a
            VariSpace contact) instead of scheduling it now.
          </span>
        </span>
      </label>

      {on && (
        <div className="space-y-2 pl-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <RecipientAutocomplete
              value={draft.signoff_recipient_name ?? ''}
              onChange={(name) => onPatch('signoff_recipient_name', name || null)}
              onPick={(r) => {
                onPatch('signoff_recipient_name', r.display_name);
                onPatch('signoff_recipient_email', r.email);
              }}
              recipients={recipients?.data ?? []}
              placeholder="Search the Vari directory, or type a name…"
              className="h-9"
            />
            <Input
              type="email"
              value={draft.signoff_recipient_email ?? ''}
              onChange={(e) => onPatch('signoff_recipient_email', e.target.value || null)}
              className="h-9"
              placeholder="name@vari.com"
            />
          </div>
          <p className="text-[11px] text-ink-faint">
            Start typing to search staff, or enter a custom name + email for a non-platform contact.
          </p>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={draft.signoff_show_cost ?? false}
              onChange={(e) => onPatch('signoff_show_cost', e.target.checked)}
            />
            Show the bid total to the recipient
            <span className="text-[11px] text-ink-faint">
              (turn off to send a date-only sign-off)
            </span>
          </label>
          <RecipientPreview
            bid={bidValue}
            showCost={draft.signoff_show_cost ?? false}
            rates={rates ?? null}
            settings={settings ?? null}
          />
        </div>
      )}
    </div>
  );
}


function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <Label>
        {label}
        {required && <span className="text-status-blocked-fg"> *</span>}
      </Label>
      {children}
    </div>
  );
}
